import { useState, useEffect, useCallback } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { ModuleIcon } from "./ModuleIcon";
import { TintChip } from "./TintChip";
import { TINT } from "../lib/moduleColors";
import { fetchRandomMissingCard } from "../lib/api";
import { trendPrice, eur } from "../lib/prices";
import { rarityInfo } from "../lib/rarity";
import { CardImage } from "./CardImage";

// Tuile d'accueil « il te manque… » : une carte tirée au hasard parmi celles
// qui restent à obtenir, en priorité dans les collections déjà commencées.
// Sert de rappel concret à côté des compteurs, qui restent abstraits.
//
// `tall` : variante large calée sur le croquis utilisateur — occupe toute la
// hauteur de la colonne des 3 compteurs empilés (le parent est un grid CSS,
// qui étire les tuiles par défaut), image plus grande, nom en gros.
export const RandomMissingCard = ({ onCardClick, tall = false }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  const draw = useCallback(() => {
    setLoading(true);
    fetchRandomMissingCard()
      .then(setCard)
      .catch(err => console.error("Carte manquante aléatoire :", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { draw(); }, [draw]);

  if (!loading && !card) return null;

  const price = card ? eur(trendPrice(card)) : null;
  const rarity = card ? rarityInfo(card.rarity) : null;
  const amberTint = TINT["bg-amber-500"];

  // « Style B » (2026-09-28, maquette approuvée) : chip teinté tenu par le
  // label (au lieu du badge rond flottant seul), lumière radiale + grain,
  // reflet holo diagonal et symbole de rareté RÉEL sur l'image (même code
  // ● ◆ ★ que le reste de l'app, cf. lib/rarity.js), boutons d'action
  // explicites plutôt qu'une carte cliquable sans autre indice.
  if (tall) {
    return (
      <div className="tcard h-full flex flex-col p-5">
        <div className="tglow" style={{ background: `radial-gradient(140px 110px at 92% -12%, rgb(${amberTint} / .35), transparent 70%)` }} />
        <div className="tgrain" />

        <div className="thead mb-4">
          <TintChip icon={Sparkles} color="bg-amber-500" />
          <span className="tlabel">Il te manque</span>
          <button onClick={draw} title="Tirer une autre carte" className="miss-refresh2">
            <RefreshCw className={loading ? "animate-spin" : ""} style={{ width: 15, height: 15 }} />
          </button>
        </div>

        {/* Image plus etroite sur telephone : a 210px elle ne laissait qu'une
            centaine de pixels au nom, tronque en « PIK… ». */}
        <div className="flex-1 flex items-center gap-4 min-h-0 relative z-10">
          {card?.image ? (
            <div className="miss-art2 shrink-0 w-[130px] sm:w-[210px]">
              <CardImage card={card} quality="low" className="w-full object-cover" />
              <div className="miss-foil" />
              {rarity?.glyph && <span className="miss-rarity-chip" style={{ color: rarity.color }}>{rarity.glyph}</span>}
              <span className="miss-num-chip">#{card.localId}</span>
            </div>
          ) : (
            <div className="shrink-0 rounded-lg bg-zinc-800 w-[130px] sm:w-[210px] aspect-[5/7]" />
          )}

          {card && (
            <div className="min-w-0 flex-1">
              <div className="text-xl sm:text-2xl font-bold text-white uppercase tracking-tight leading-tight line-clamp-2 stat-value" title={card.name}>
                {card.name}
              </div>
              <div className="text-sm text-zinc-400 truncate mt-1">
                {card.set?.name}{rarity?.label ? ` · ${rarity.label}` : ""}
              </div>
              {price && (
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="text-xl sm:text-2xl font-bold text-amber-400 stat-value">{price}</span>
                  <span className="text-[11px] uppercase tracking-wider text-zinc-600">Prix tendance</span>
                </div>
              )}
              <div className="flex gap-2.5 mt-4">
                <button className="miss-btn primary" onClick={() => onCardClick(card)}>Ouvrir la fiche</button>
                <button className="miss-btn ghost" onClick={draw}>
                  <RefreshCw className={loading ? "animate-spin" : ""} style={{ width: 13, height: 13 }} />
                  Une autre
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="stat-widget flex items-center gap-3">
      {/* Halo décoratif : `pointer-events-none` est INDISPENSABLE — il est en
          position absolue au-dessus du bouton « rafraîchir » (même coin) et
          avalait le clic, ce qui donnait un bouton mort. */}
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-[0.15] bg-amber-500 pointer-events-none"></div>

      {card?.image ? (
        <button onClick={() => onCardClick(card)} title="Ouvrir la fiche"
          className="shrink-0 rounded-md overflow-hidden ring-1 ring-white/10" style={{ width: 58 }}>
          <CardImage card={card} quality="low" className="w-full object-cover" />
        </button>
      ) : (
        <div className="shrink-0 rounded-md bg-zinc-800" style={{ width: 58, height: 81 }} />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Il te manque</span>
        </div>
        {card && (
          <>
            <button onClick={() => onCardClick(card)}
              className="block text-left text-base font-bold text-white truncate w-full hover:underline"
              title={card.name}>
              {card.name}
            </button>
            <div className="text-xs text-zinc-500 truncate">
              {card.set?.name} · #{card.localId}{price ? ` · ${price}` : ""}
            </div>
          </>
        )}
      </div>

      <button onClick={draw} title="Tirer une autre carte"
        className="shrink-0 p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800">
        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
};
